// Role home: approved heading, intro, tasks and boundary for each granted experience (via the
// i18n catalogue; PD-016 lists the few production adaptations of prototype wording).
import { Link } from 'react-router';
import { useI18n } from '../i18n.tsx';
import { NAV, ROLE_COPY } from '../nav.ts';
import { useSession } from '../session.tsx';

function routeLink(route: string): string | null {
  const item = NAV[route.replace(/^@/, '')];
  if (!item || item.milestone) return null;
  return item.route === 'home' ? '/' : `/${item.route}`;
}

export function HomePage() {
  const { session } = useSession();
  const { t, tk } = useI18n();
  const ids = [...new Set(session.experiences.map((e) => e.id))].filter((id) => ROLE_COPY[id]);
  if (ids.length === 0) {
    return (
      <>
        <div className="eyebrow">{t('home.eyebrow', { name: session.organisation.name })}</div>
        <h1>{t('home.none.title')}</h1>
        <p style={{ marginBlockStart: 12, maxInlineSize: 640 }}>{t('home.none.body')}</p>
      </>
    );
  }
  return (
    <>
      {ids.map((id, index) => {
        const role = ROLE_COPY[id]!;
        const primaryHref = routeLink(role.primary[1]);
        return (
          <section key={id} aria-labelledby={`role-${id}`} style={index > 0 ? { marginBlockStart: 48 } : undefined}>
            <div className="pagehead">
              <div>
                <div className="eyebrow">{t('home.eyebrow', { name: tk(`role.${id}.title`) })}</div>
                <h1 id={`role-${id}`}>{tk(`role.${id}.heading`)}</h1>
                <p>{tk(`role.${id}.intro`)}</p>
              </div>
            </div>
            {primaryHref ? (
              <Link className="button primary" to={primaryHref}>
                {tk(`role.${id}.primary`)}
              </Link>
            ) : null}
            {role.tasks.length ? (
              <div className="task-grid">
                {role.tasks.map(([route], i) => {
                  const href = routeLink(route);
                  return (
                    <div className="task-card" key={route + i}>
                      <h2>{tk(`role.${id}.task${i + 1}.title`)}</h2>
                      <p>{tk(`role.${id}.task${i + 1}.body`)}</p>
                      {href ? <Link to={href}>{t('home.openWorkspace')}</Link> : <span className="later">{t('home.notYet')}</span>}
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="scope-note">
              <strong>{t('home.boundary')}</strong>
              <p>{tk(`role.${id}.boundary`)}</p>
            </div>
          </section>
        );
      })}
    </>
  );
}

export function PlannedPage({ route }: { route: string }) {
  const { t } = useI18n();
  const item = NAV[route];
  const label = item ? t(item.labelKey) : route;
  return (
    <>
      <div className="eyebrow">Passage / {label}</div>
      <h1>{label}</h1>
      <div className="scope-note" style={{ marginBlockStart: 24 }}>
        <strong>{t('planned.title')}</strong>
        <p>{t('planned.body', { milestone: item?.milestone ?? '—', modules: item?.modules.join(', ') ?? '—' })}</p>
      </div>
    </>
  );
}

export function NotFoundPage() {
  const { t } = useI18n();
  return (
    <>
      <div className="eyebrow">Passage</div>
      <h1>{t('notFound.title')}</h1>
      <p style={{ marginBlockStart: 12 }}>
        {t('notFound.body')} <Link to="/">{t('notFound.back')}</Link>
      </p>
    </>
  );
}

export function DeniedPage() {
  const { t } = useI18n();
  return (
    <>
      <div className="eyebrow">Passage</div>
      <h1>{t('denied.title')}</h1>
      <p style={{ marginBlockStart: 12, maxInlineSize: 640 }}>{t('denied.body')}</p>
    </>
  );
}
